import { pick } from "es-toolkit";
import { useCallback } from "react";

import {
  getComputedInset,
  type Collision,
  type DragDropEventHandlers,
  type UniqueIdentifier,
} from "@skilitics/drag-drop";

import { DND_CREATABLE_ID, DND_TEMPLATE_ID } from "./dndConstants";
import type { DragEndEventResult } from "./dndEventTypes";
import { collisionDataSchema, draggableDataSchema, droppableDataSchema } from "./dndSchemas";
import { isResizeableId } from "./dndUtils";

const slotName = "content";

type Options = {
  rootContainerId: number;
};

type Sortable = { group?: UniqueIdentifier; index?: number };

type DragSource = {
  id: UniqueIdentifier;
  data: Record<string, unknown>;
  sortable?: Sortable;
};

type DragTarget = {
  id: UniqueIdentifier;
  data: Record<string, unknown>;
  disabled: boolean;
  sortable?: Sortable;
};

export type DragEndEvent = {
  source: DragSource | null;
  target: DragTarget | null;
  collisions: Collision[] | null;
  transform: { x: number; y: number };
  activatorEvent: Event | null;
};

export const useOnDragEnd = (
  options: Options,
): ((...args: Parameters<DragDropEventHandlers["onDragEnd"]>) => DragEndEventResult) => {
  return useCallback(
    (event, manager) => {
      const { source, target, transform, activatorEvent } = event.operation;
      const collisions = manager?.collisionObserver.collisions ?? [];
      return onDragEnd(
        {
          source: source as DragSource | null,
          target: target as DragTarget | null,
          collisions,
          transform,
          activatorEvent,
        },
        options,
      );
    },
    [options],
  );
};

export const onDragEnd = (
  { source, target, collisions, transform, activatorEvent }: DragEndEvent,
  { rootContainerId }: Options,
): DragEndEventResult => {
  if (!source) return;
  if (isResizeableId(source.id)) return;

  const parsedActiveData = draggableDataSchema.parse(source.data);
  const { node: activeNode, isAbsolutelyPositioned, inLayersPanel } = parsedActiveData;

  // Case: Element with absolute positioning dragged on canvas (not in layers panel) - update position
  // NOTE: this is temporary POC behaviour. The ideal solution involves transient state
  // and updating the position on drag move so we see the X/Y coordinates update in the properties
  // panel in real time during drag.
  // This bypasses normal collision/over checks since the element just repositions within its parent.
  if (
    activeNode.type === "instance" &&
    isAbsolutelyPositioned &&
    !inLayersPanel &&
    transform &&
    (transform.x !== 0 || transform.y !== 0)
  ) {
    const positionedElement = findPositionedElement(activatorEvent?.target ?? null);
    const computedInset = getComputedInset(positionedElement);
    return {
      eventType: "position",
      nodeId: activeNode.id,
      delta: { x: transform.x, y: transform.y },
      computedInset,
    };
  }

  if (!target || !collisions?.length || target.disabled || source.id === target.id) return;

  const { data: collisionData } = collisions[0];
  const parsedCollisionData = collisionDataSchema.safeParse(collisionData);

  // Failed to determine information from collision, so do nothing.
  if (!parsedCollisionData.success) return;

  const { indicator } = parsedCollisionData.data;
  const insertPosition =
    indicator === "inside" ? indicator : ["insertTop", "insertLeft"].includes(indicator) ? "before" : "after";

  const {
    node: { id: overNodeId, typeId: overTypeId, ancestorIds: overAncestorIds },
  } = droppableDataSchema.parse(target.data);

  const overCanHaveChildren = overTypeId === "flexbox.container";
  const overIsRoot = overNodeId === rootContainerId;

  // Disable moving a container into a descendant of itself.
  if (activeNode.type === "instance" && overAncestorIds?.includes(activeNode.id)) return;

  // The root container has no parent, so before/after makes no sense — treat as inside.
  const effectiveInsertPosition = overIsRoot && insertPosition !== "inside" ? "inside" : insertPosition;

  // Dragged item was a new component or template.
  if (activeNode.type === DND_CREATABLE_ID || activeNode.type === DND_TEMPLATE_ID) {
    const create =
      activeNode.type === DND_CREATABLE_ID
        ? pick(activeNode, ["type", "typeId", "definitionId"])
        : pick(activeNode, ["type", "typeId", "templateId"]);

    // Item was dropped inside a flexbox.container
    if (overCanHaveChildren && effectiveInsertPosition === "inside") {
      return {
        eventType: "add",
        targetNodeId: overNodeId,
        create,
      };
    }

    const overIndex = target.sortable?.index ?? 0;
    const overContainerIdStr = target.sortable?.group != null ? String(target.sortable.group) : "";

    return {
      eventType: "add",
      targetNodeId: overIsRoot ? rootContainerId : Number(overContainerIdStr),
      create,
      index: effectiveInsertPosition !== "before" ? overIndex + 1 : overIndex,
    };
  }

  const activeIndex = source.sortable?.index ?? 0;
  const activeContainerIdStr = source.sortable?.group != null ? String(source.sortable.group) : "";

  // Dragged item is an existing component.
  const activeContainerId = Number(activeContainerIdStr) || rootContainerId;
  const fromSlot = {
    parentId: activeContainerId,
    index: activeIndex,
    name: slotName,
  };

  // Case: Dragged item is dragged over a container, should be put inside (at start).
  if (overCanHaveChildren && effectiveInsertPosition === "inside") {
    // It's already inside the container, so don't move it.
    if (activeContainerId === overNodeId) return;

    const toSlot = {
      parentId: overNodeId,
      name: slotName,
      index: 0,
    };

    return {
      eventType: "move",
      nodeId: activeNode.id,
      from: fromSlot,
      to: toSlot,
    };
  }

  const overIndex = target.sortable?.index ?? 0;
  const overContainerIdStr = target.sortable?.group != null ? String(target.sortable.group) : "";

  let newIndex = overIndex;

  // Case: Dragged item is in the same container as the item being dragged over, and should be reordered.
  if (activeContainerIdStr === overContainerIdStr) {
    if (activeIndex === newIndex) return;
    if (activeIndex > overIndex) {
      newIndex = effectiveInsertPosition !== "before" ? overIndex + 1 : overIndex;
    } else {
      newIndex = effectiveInsertPosition !== "before" ? overIndex : overIndex - 1;
    }

    return {
      eventType: "reorder",
      nodeId: activeNode.id,
      index: newIndex,
    };
  }

  // The root container's sortable group is "0" (no ancestors), and dnd-kit may also
  // use "Sortable" as the group. Both resolve to invalid node IDs, so fall back to rootContainerId.
  const overContainerId = Number(overContainerIdStr) || rootContainerId;

  // Case: Dragged item is in a different container than the item being dragged over, and should be moved.
  const toSlot = {
    parentId: overContainerId,
    name: slotName,
    index: effectiveInsertPosition !== "before" ? newIndex + 1 : newIndex,
  };

  return {
    eventType: "move",
    nodeId: activeNode.id,
    from: fromSlot,
    to: toSlot,
  };
};

/**
 * Walk up from the event target to find the nearest positioned element.
 * Needed because DragEndEvent doesn't expose the draggable DOM node —
 * only activatorEvent.target (which may be any child element).
 */
const findPositionedElement = (target: EventTarget | null): HTMLElement | null => {
  if (!(target instanceof HTMLElement)) return null;
  let element: HTMLElement | null = target;
  while (element) {
    const position = getComputedStyle(element).position;
    if (position === "absolute" || position === "fixed") return element;
    element = element.parentElement;
  }
  return null;
};
